import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { MapPin, Search, Navigation, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import SubdistrictSearch from '../components/SubdistrictSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reverse geocode via our backend proxy
async function reverseGeocode(lat, lng) {
  try {
    const res = await axios.get(`${API}/geocode/reverse`, { params: { lat, lon: lng } });
    return res.data;
  } catch { return null; }
}

// Forward geocode via our backend proxy
async function forwardGeocode(query) {
  try {
    const res = await axios.get(`${API}/geocode/search`, { params: { q: query } });
    return res.data;
  } catch { return []; }
}

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const markerIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// Recenter map when coordinates change
function MapRecenter({ lat, lng, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng) map.setView([lat, lng], zoom || 16, { animate: true });
  }, [lat, lng, zoom, map]);
  return null;
}

// Handle map click to place pin
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({ click(e) { onLocationSelect(e.latlng.lat, e.latlng.lng); } });
  return null;
}

// Draggable marker
function DraggableMarker({ position, onDragEnd }) {
  const markerRef = useRef(null);
  const eventHandlers = useMemo(() => ({
    dragend() {
      const m = markerRef.current;
      if (m) { const ll = m.getLatLng(); onDragEnd(ll.lat, ll.lng); }
    },
  }), [onDragEnd]);
  return <Marker draggable position={position} icon={markerIcon} ref={markerRef} eventHandlers={eventHandlers} />;
}

export default function AddressFormDialog({ open, onOpenChange, addressForm, setAddressForm, onSave, lockKecamatan, lockedKecamatanLabel, lockedKecamatanReason, showSaveToProfile, saveToProfile, setSaveToProfile }) {
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [searchingAddress, setSearchingAddress] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [cityQuery, setCityQuery] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [searchingCity, setSearchingCity] = useState(false);
  const [locatingGPS, setLocatingGPS] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  const addressSearchRef = useRef(null);
  const addressDropRef = useRef(null);
  const cityInputRef = useRef(null);
  const cityDropRef = useRef(null);
  const searchTimeout = useRef(null);
  const addressTimeout = useRef(null);

  // Snapshot the form when the dialog opens so we can revert unsaved edits when
  // the user closes via X / Escape / outside-click. A second person on a shared
  // device shouldn't see (or accidentally inherit) a previous user's half-typed
  // address. Saving via the form's Save button bypasses the revert.
  const initialFormRef = useRef(null);
  const savedRef = useRef(false);
  useEffect(() => {
    if (open) {
      initialFormRef.current = { ...addressForm };
      savedRef.current = false;
      // Also wipe ephemeral search state so the dropdowns/suggestions don't
      // bleed across opens.
      setAddressQuery('');
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setCitySuggestions([]);
      setCityQuery('');
      setShowCityDropdown(false);
    }
    // We intentionally only react to `open` transitions — the inputs above are
    // setters that don't need to be in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDialogOpenChange = (next) => {
    if (!next && !savedRef.current && initialFormRef.current) {
      // Closing without a save → revert any typed changes.
      setAddressForm(initialFormRef.current);
    }
    onOpenChange(next);
  };

  // ── Phone formatting ──
  const handlePhoneChange = (value) => {
    let digits = value.replace(/[^\d]/g, '');
    if (digits.startsWith('0')) digits = digits.slice(1);
    if (digits.startsWith('62')) digits = digits.slice(2);
    digits = digits.slice(0, 13);
    setAddressForm(a => ({ ...a, phone: digits ? `+62${digits}` : '' }));
  };

  const displayPhone = () => {
    const phone = addressForm.phone || '';
    if (phone.startsWith('+62')) return phone.slice(3);
    return phone.replace(/^\+?62/, '');
  };

  // ── Address search (Nominatim forward geocode) ──
  const handleAddressSearch = useCallback(async (query) => {
    if (query.length < 3) { setAddressSuggestions([]); return; }
    setSearchingAddress(true);
    const results = await forwardGeocode(query);
    setAddressSuggestions(results);
    setShowAddressSuggestions(results.length > 0);
    setSearchingAddress(false);
  }, []);

  const handleAddressQueryChange = (value) => {
    setAddressQuery(value);
    if (addressTimeout.current) clearTimeout(addressTimeout.current);
    addressTimeout.current = setTimeout(() => handleAddressSearch(value), 500);
  };

  const selectAddressSuggestion = (suggestion) => {
    const addr = suggestion.address || {};
    const lat = parseFloat(suggestion.lat);
    const lng = parseFloat(suggestion.lon);

    const city = addr.city || addr.town || addr.county || addr.municipality || '';
    const province = addr.state || '';
    const postcode = addr.postcode || '';

    // Only update map pin, city, province, postal — never touch address_line
    setAddressForm(a => ({
      ...a,
      city: city,
      province: province,
      postal_code: postcode || a.postal_code,
      latitude: lat,
      longitude: lng,
    }));
    setCityQuery(city);
    setAddressQuery(suggestion.display_name);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
    setMapKey(k => k + 1);
  };

  // ── Reverse geocode on pin move/click ──
  // Only fills empty fields — never overwrites user-typed Street Address
  const handleMapPinChange = useCallback(async (lat, lng) => {
    setAddressForm(a => ({ ...a, latitude: lat, longitude: lng }));
    const data = await reverseGeocode(lat, lng);
    if (data && data.address) {
      const addr = data.address;
      const city = addr.city || addr.town || addr.county || addr.municipality || '';
      const province = addr.state || '';
      const postcode = addr.postcode || '';

      setAddressForm(a => ({
        ...a,
        // Never overwrite street address — that's the user's authoritative input
        city: city || a.city,
        province: province || a.province,
        postal_code: postcode || a.postal_code,
        latitude: lat,
        longitude: lng,
      }));
      if (city) setCityQuery(prev => prev || city);
    }
  }, [setAddressForm, cityQuery]);

  // ── Use My Location (GPS) ──
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported by your browser'); return; }
    setLocatingGPS(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        await handleMapPinChange(latitude, longitude);
        setMapKey(k => k + 1);
        setLocatingGPS(false);
        toast.success('Location detected!');
      },
      () => { setLocatingGPS(false); toast.error('Could not get your location. Please allow location access.'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // ── City autocomplete (internal DB) ──
  const searchCities = useCallback(async (query) => {
    if (query.length < 3) { setCitySuggestions([]); return; }
    setSearchingCity(true);
    try {
      const res = await axios.get(`${API}/cities/search`, { params: { q: query } });
      setCitySuggestions(res.data);
      setShowCityDropdown(res.data.length > 0);
    } catch { setCitySuggestions([]); }
    setSearchingCity(false);
  }, []);

  const handleCityInputChange = (value) => {
    setCityQuery(value);
    setAddressForm(a => ({ ...a, city: value, province: '' }));
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchCities(value), 300);
  };

  const selectCity = (city, province) => {
    setCityQuery(city);
    setAddressForm(a => ({ ...a, city, province }));
    setShowCityDropdown(false);
    setCitySuggestions([]);
  };

  // ── Close dropdowns on outside click ──
  useEffect(() => {
    const handler = (e) => {
      if (addressDropRef.current && !addressDropRef.current.contains(e.target) &&
          addressSearchRef.current && !addressSearchRef.current.contains(e.target)) {
        setShowAddressSuggestions(false);
      }
      if (cityDropRef.current && !cityDropRef.current.contains(e.target) &&
          cityInputRef.current && !cityInputRef.current.contains(e.target)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync state when dialog opens with existing data
  useEffect(() => {
    if (open) {
      if (addressForm.city) setCityQuery(addressForm.city);
    }
  }, [open, addressForm.city]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!addressForm.full_name || !addressForm.phone || !addressForm.address_line) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!addressForm.subdistrict_id) {
      toast.error('Please select a subdistrict (kecamatan)');
      return;
    }
    if (!addressForm.latitude || !addressForm.longitude) {
      toast.error('Please pin your location on the map');
      return;
    }
    savedRef.current = true;
    onSave(e);
  };

  const hasPin = addressForm.latitude && addressForm.longitude;
  const defaultLat = addressForm.latitude || -6.2088;
  const defaultLng = addressForm.longitude || 106.8456;

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-lg mx-4 max-h-[90vh] overflow-y-auto" data-testid="address-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#2C2A29]">Delivery Address</DialogTitle>
          <DialogDescription className="font-body text-[#78716C]">
            Search your address, then fine-tune the pin on the map
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-1">
          {/* Full Name */}
          <div>
            <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Full Name</label>
            <input type="text" required value={addressForm.full_name || ''}
              onChange={e => setAddressForm(a => ({ ...a, full_name: e.target.value }))}
              className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
              placeholder="Full Name" data-testid="address-full_name" />
          </div>

          {/* Phone with +62 */}
          <div>
            <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Phone</label>
            <div className="flex">
              <span className="inline-flex items-center px-3 bg-cream-100 border border-r-0 border-cream-100 rounded-l-xl text-sm font-body text-[#78716C] font-semibold select-none" data-testid="phone-prefix">+62</span>
              <input type="tel" required value={displayPhone()}
                onChange={e => handlePhoneChange(e.target.value)}
                className="flex-1 bg-cream-50 border border-cream-100 rounded-r-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="812 3456 7890" data-testid="address-phone" />
            </div>
          </div>

          {/* Street Address — authoritative shipping address */}
          <div>
            <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Street Address <span className="text-[#F4C2C2]">*</span></label>
            <input type="text" required value={addressForm.address_line || ''}
              onChange={e => setAddressForm(a => ({ ...a, address_line: e.target.value }))}
              className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
              placeholder="Jl. Sudirman No. 10, RT 01/RW 02, Kel. ..." data-testid="address-address_line" />
          </div>

          {/* ── Find Area (search) — below street address ── */}
          <div className="relative">
            <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Find Area (format: kecamatan, jalan)</label>
            <div className="relative" ref={addressSearchRef}>
              <input type="text" value={addressQuery}
                onChange={e => handleAddressQueryChange(e.target.value)}
                onFocus={() => { if (addressSuggestions.length > 0) setShowAddressSuggestions(true); }}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 pr-9 font-body text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-blush-200 focus:border-transparent"
                placeholder="e.g. Menteng, Jl. Sudirman"
                data-testid="address-search-input" />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {searchingAddress ? (
                  <Loader2 size={14} className="text-[#F4C2C2] animate-spin" />
                ) : (
                  <Search size={14} className="text-[#A89F91]" />
                )}
              </div>
            </div>
            <p className="text-xs font-body text-[#A89F91] mt-1">Fill this in to find the location on the map</p>
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div ref={addressDropRef} className="mt-1 w-full bg-white border border-cream-100 rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid="address-suggestions">
                {addressSuggestions.map((s, i) => (
                  <button key={s.place_id || i} type="button"
                    onClick={() => selectAddressSuggestion(s)}
                    className="w-full text-left px-3 py-2.5 hover:bg-[#FFF5F5] transition-colors border-b border-cream-50 last:border-0 flex items-start gap-2"
                    data-testid={`address-suggestion-${i}`}>
                    <MapPin size={14} className="text-[#F4C2C2] mt-0.5 flex-shrink-0" />
                    <span className="font-body text-sm text-[#2C2A29] leading-snug">{s.display_name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Map — Pin Your Location ── */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-body font-medium text-[#78716C] flex items-center gap-1">
                <MapPin size={12} /> Pin Your Location
              </label>
              <button type="button" onClick={handleUseMyLocation} disabled={locatingGPS}
                className="text-xs font-body font-semibold text-[#C5A880] hover:text-[#2C2A29] transition-colors flex items-center gap-1 disabled:opacity-50"
                data-testid="use-my-location-btn">
                {locatingGPS ? <Loader2 size={12} className="animate-spin" /> : <Navigation size={12} />}
                {locatingGPS ? 'Detecting...' : 'Use My Location'}
              </button>
            </div>
            <p className="text-xs font-body text-[#A89F91] mb-2">Pin your delivery point for the courier. This won't change your street address above.</p>
            <div className="rounded-2xl overflow-hidden border border-cream-100 h-[220px]" data-testid="address-map">
              <MapContainer key={mapKey} center={[defaultLat, defaultLng]} zoom={hasPin ? 16 : 5}
                style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <MapClickHandler onLocationSelect={handleMapPinChange} />
                {hasPin && (
                  <>
                    <DraggableMarker position={[addressForm.latitude, addressForm.longitude]} onDragEnd={handleMapPinChange} />
                    <MapRecenter lat={addressForm.latitude} lng={addressForm.longitude} zoom={16} />
                  </>
                )}
              </MapContainer>
            </div>
            {hasPin && (
              <p className="text-xs font-body text-green-600 mt-1.5 flex items-center gap-1" data-testid="pin-confirmation">
                <MapPin size={12} /> Location pinned ({addressForm.latitude.toFixed(4)}, {addressForm.longitude.toFixed(4)})
              </p>
            )}
          </div>

          {/* Subdistrict (Kecamatan) */}
          <div>
            <label className="block text-xs font-body font-medium text-[#78716C] mb-1">
              Subdistrict (Kecamatan) <span className="text-[#F4C2C2]">*</span>
              {lockKecamatan && <span className="ml-1 text-[10px] font-normal text-[#A89F91]">(locked for this order)</span>}
            </label>
            {lockKecamatan ? (
              <div data-testid="address-kecamatan-locked" className="w-full bg-cream-100/60 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#78716C] cursor-not-allowed">
                📍 {lockedKecamatanLabel || addressForm.subdistrict_label || 'Kecamatan locked'}
              </div>
            ) : (
              <SubdistrictSearch
                value={addressForm.subdistrict_id}
                label={addressForm.subdistrict_label}
                onChange={({ id, label, city, province }) => setAddressForm(a => ({
                  ...a,
                  subdistrict_id: id,
                  subdistrict_label: label,
                  city: city || a.city,
                  province: province || a.province,
                }))}
                placeholder="Type kecamatan name..."
              />
            )}
            {lockKecamatan && (
              <p className="text-xs font-body text-[#A89F91] mt-1 leading-relaxed" data-testid="kecamatan-lock-reason">
                {lockedKecamatanReason || "This pickup uses the kecamatan from when this order was placed. To change your default shipping origin for future orders, edit it from your profile settings."}
              </p>
            )}
          </div>

          {/* Province (auto-filled) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">City</label>
              <input type="text" value={addressForm.city || ''}
                readOnly
                className="w-full bg-cream-100/60 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#78716C] cursor-not-allowed"
                placeholder="Auto-filled" data-testid="address-city" />
            </div>
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1">Province</label>
              <input type="text" value={addressForm.province || ''}
                readOnly
                className="w-full bg-cream-100/60 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#78716C] cursor-not-allowed"
                placeholder="Auto-filled" data-testid="address-province" />
            </div>
          </div>

          {showSaveToProfile && (
            <label className="flex items-start gap-2 cursor-pointer mt-1 px-1 select-none" data-testid="save-to-profile-toggle">
              <input
                type="checkbox"
                checked={!!saveToProfile}
                onChange={e => setSaveToProfile && setSaveToProfile(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[#9CAF88] cursor-pointer"
                data-testid="save-to-profile-checkbox"
              />
              <div className="flex-1">
                <p className="text-xs font-body font-medium text-[#2C2A29]">Save as my default address</p>
                <p className="text-xs font-body text-[#A89F91] leading-relaxed mt-0.5">
                  Uncheck if this is a one-time delivery (e.g., gift to a friend) — your profile won't be updated.
                </p>
              </div>
            </label>
          )}

          <button type="submit"
            className="w-full bg-[#F4C2C2] text-[#2C2A29] rounded-full px-6 py-3 font-body font-semibold hover:bg-[#ECAEAE] transition-all mt-2"
            data-testid="save-address-btn">
            {showSaveToProfile && !saveToProfile ? 'Use this delivery address' : 'Save Address'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
